/* eslint-disable */
import React, { useState, useCallback, useRef, useEffect } from "react";
import { Box, Typography, Grid, Paper, Button, Divider, Checkbox, FormControlLabel, Stack, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import { DynamicFieldAdvanced } from "../../../../../../components/common/input/DynamicFieldAdvanced";
import { useDispatch, useSelector } from "react-redux";
import { setBasicDetails, setBasicDetailsComplete, setStepCompletion, updateAddressField } from "../../../../../../store/index";
import { saveEmployeePersonalInfo } from "../../../../../../api/employeeApi";
import states from "../../../../../../utils/states.json";
import { useNotification } from "../../../../../../hooks/useNotification";

const visuallyHiddenStyles = {
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
};

// FileList component for displaying uploaded documents. We define it here to
// avoid colliding with the browser's built-in FileList interface (which is
// not constructible and was causing an "Illegal constructor" error when React
// attempted to render <FileList /> without a local definition).
const FileList = ({ files, onRemove }) => {
  if (!files || files.length === 0) return null;
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1, gap: 1 }}>
      {files.map((file, idx) => (
        <Box
          key={idx}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: '20px',
            bgcolor: 'rgba(16, 185, 129, 0.06)',
            border: '1px solid rgba(16, 185, 129, 0.15)',
            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.02)',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'rgba(16, 185, 129, 0.1)',
            }
          }}
        >
          {file.url ? (
            <Typography
              variant="caption"
              onClick={async (e) => {
                e.preventDefault();
                try {
                  const response = await fetch(`/api/onboarding/proxy-download?url=${encodeURIComponent(file.url)}`);
                  if (!response.ok) throw new Error('Proxy failed');
                  const blob = await response.blob();
                  const link = document.createElement('a');
                  link.href = window.URL.createObjectURL(blob);
                  link.download = file.name || file.url.split('/').pop().split('?')[0];
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                } catch (err) {
                  window.open(file.url, '_blank');
                }
              }}
              sx={{
                color: "#059669",
                cursor: "pointer",
                textDecoration: "none",
                fontWeight: 700,
                fontSize: '0.75rem',
                "&:hover": { textDecoration: "underline" }
              }}
            >
              {file.name}
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ color: '#065F46', fontWeight: 700, fontSize: '0.75rem' }}>{file.name}</Typography>
          )}
          <IconButton size="small" onClick={() => onRemove(idx)} sx={{ p: 0.25, ml: 0.5, color: '#059669', '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.15)' } }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      ))}
    </Stack>
  );
};


const PersonalDetails = ({ onNext, preFilledData, employeeId }) => {
  const dispatch = useDispatch();
  const { showNotification, Notification } = useNotification();
  const formData = useSelector((state) => state.basicDetails.formData) || {};
  const isComplete = useSelector((state) => state.basicDetails.isComplete);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Local state for file management
  const [aadharFrontFiles, setAadharFrontFiles] = useState([]);
  const [aadharBackFiles, setAadharBackFiles] = useState([]);
  const [panFiles, setPanFiles] = useState([]);
  const [passportFiles, setPassportFiles] = useState([]);
  const [rentalFiles, setRentalFiles] = useState([]);
  const [disabilityCertificateFiles, setDisabilityCertificateFiles] = useState([]);

  // Ref to track previous values for change detection
  const previousValuesRef = useRef({});


  // // Fixed form initialization - prevent infinite loop
  useEffect(() => {

    if (preFilledData && Object.keys(preFilledData).some(key => preFilledData[key])) {
      // Check if we need to update (prevent infinite loop)
      const needsUpdate = Object.entries(preFilledData).some(([key, value]) => {
        return value && value.trim() !== '' && formData[key] !== value;
      });

      if (needsUpdate) {
        // Load new data without resetting first
        const validPreFilledData = Object.entries(preFilledData).reduce((acc, [key, value]) => {
          if (value && value.trim() !== '') {
            acc[key] = value;
          }
          return acc;
        }, {});

        if (Object.keys(validPreFilledData).length > 0) {
          dispatch(setBasicDetails(validPreFilledData));
        }
      }
    }
  }, [preFilledData]); // Only depend on preFilledData, not formData

  // Sync files from Redux to local state
  useEffect(() => {
    if (formData.aadharFrontFiles) {
      setAadharFrontFiles(formData.aadharFrontFiles);
    }
    if (formData.aadharBackFiles) {
      setAadharBackFiles(formData.aadharBackFiles);
    }
    if (formData.panFiles) {
      setPanFiles(formData.panFiles);
    }
    if (formData.passportFiles) {
      setPassportFiles(formData.passportFiles);
    }
    if (formData.rentalFiles) {
      setRentalFiles(formData.rentalFiles);
    }
    if (formData.disabilityCertificateFiles) {
      setDisabilityCertificateFiles(formData.disabilityCertificateFiles);
    }
  }, [formData.aadharFrontFiles, formData.aadharBackFiles, formData.panFiles, formData.passportFiles, formData.rentalFiles, formData.disabilityCertificateFiles]);

  // Fixed form initialization - prevent infinite loop
  const hasLoadedPreFilled = useRef(false);
  useEffect(() => {
    if (!hasLoadedPreFilled.current && preFilledData && Object.keys(preFilledData).some(key => preFilledData[key])) {
      const validPreFilledData = Object.entries(preFilledData).reduce((acc, [key, value]) => {
        if (value && typeof value === 'string' && value.trim() !== '') {
          acc[key] = value;
        } else if (value !== undefined && value !== null) {
          acc[key] = value;
        }
        return acc;
      }, {});

      if (Object.keys(validPreFilledData).length > 0) {
        dispatch(setBasicDetails(validPreFilledData));
        hasLoadedPreFilled.current = true;
      }
    }
  }, [preFilledData, dispatch]);

  // Local state for Nominees
  const [nominees, setNominees] = useState([]);
  const [nomineeFrontFiles, setNomineeFrontFiles] = useState({});
  const [nomineeBackFiles, setNomineeBackFiles] = useState({});

  const hasLoadedNominees = useRef(false);
  useEffect(() => {
    if (!hasLoadedNominees.current) {
      if (formData.nominees && Array.isArray(formData.nominees) && formData.nominees.length > 0) {
        setNominees(formData.nominees);

        const fronts = {};
        const backs = {};
        formData.nominees.forEach((n, idx) => {
          if (n.aadhaarFrontPath) {
            fronts[idx] = [{ name: n.aadhaarFrontPath.split('/').pop() || "Aadhaar Front Copy", url: n.aadhaarFrontPath, isExisting: true }];
          }
          if (n.aadhaarBackPath) {
            backs[idx] = [{ name: n.aadhaarBackPath.split('/').pop() || "Aadhaar Back Copy", url: n.aadhaarBackPath, isExisting: true }];
          }
        });
        setNomineeFrontFiles(fronts);
        setNomineeBackFiles(backs);
        hasLoadedNominees.current = true;
      } else if (formData.nominees !== undefined) {
        const defaultNominee = {
          nomineeName: "",
          contactNo: "",
          nomineeDob: "",
          relation: "",
          aadhaarNumber: "",
          sharePercentage: 100.00,
          nomineeType: "Normal"
        };
        setNominees([defaultNominee]);
        dispatch(setBasicDetails({ nominees: [defaultNominee] }));
        hasLoadedNominees.current = true;
      }
    }
  }, [formData.nominees, dispatch]);

  const handleAddNominee = useCallback(() => {
    let updatedNominees = [...nominees];
    updatedNominees.push({
      nomineeName: "",
      contactNo: "",
      nomineeDob: "",
      relation: "",
      aadhaarNumber: "",
      sharePercentage: "",
      nomineeType: "Normal"
    });
    setNominees(updatedNominees);
    dispatch(setBasicDetails({ nominees: updatedNominees }));
  }, [nominees, dispatch]);

  const handleRemoveNominee = useCallback((index) => {
    const filteredNominees = nominees.filter((_, i) => i !== index);

    const nextFronts = { ...nomineeFrontFiles };
    const nextBacks = { ...nomineeBackFiles };
    delete nextFronts[index];
    delete nextBacks[index];
    setNomineeFrontFiles(nextFronts);
    setNomineeBackFiles(nextBacks);

    setNominees(filteredNominees);
    dispatch(setBasicDetails({ nominees: filteredNominees }));
  }, [nominees, nomineeFrontFiles, nomineeBackFiles, dispatch]);

  const handleNomineeChange = useCallback((index, field, value) => {
    let updatedNominees = [...nominees];
    if (field === 'contactNo') {
      value = value.replace(/\D/g, '').slice(0, 10);
    } else if (field === 'aadhaarNumber') {
      value = value.replace(/\D/g, '').slice(0, 12);
    }

    updatedNominees[index] = { ...updatedNominees[index], [field]: value };
    setNominees(updatedNominees);
    dispatch(setBasicDetails({ nominees: updatedNominees }));

    // Clear field-specific errors
    setErrors(prev => {
      const next = { ...prev };
      delete next[`nomineeName_${index}`];
      delete next[`nomineeRelation_${index}`];
      delete next[`nomineeContact_${index}`];
      delete next[`nomineeDob_${index}`];
      delete next[`nomineeAadhaarNumber_${index}`];
      delete next[`nomineeSharePercentage_${index}`];
      delete next.nomineesShare;
      return next;
    });
  }, [nominees, dispatch]);

  const handleNomineeFrontUpload = useCallback((index, event) => {
    const files = Array.from(event.target.files);
    setNomineeFrontFiles(prev => ({ ...prev, [index]: files }));
  }, []);

  const handleNomineeBackUpload = useCallback((index, event) => {
    const files = Array.from(event.target.files);
    setNomineeBackFiles(prev => ({ ...prev, [index]: files }));
  }, []);

  const handleRemoveNomineeFrontFile = useCallback((index) => {
    setNomineeFrontFiles(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    if (nominees[index]?.aadhaarFrontPath) {
      handleNomineeChange(index, 'aadhaarFrontPath', '');
    }
  }, [nominees, handleNomineeChange]);

  const handleRemoveNomineeBackFile = useCallback((index) => {
    setNomineeBackFiles(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    if (nominees[index]?.aadhaarBackPath) {
      handleNomineeChange(index, 'aadhaarBackPath', '');
    }
  }, [nominees, handleNomineeChange]);

  const aadharFileInputRef = useRef();
  const panFileInputRef = useRef();
  const passportFileInputRef = useRef();
  const rentalFileInputRef = useRef();

  const handleFieldBlur = useCallback((name, value) => {
    // Mark field as touched when user leaves it
    setTouched(prev => ({ ...prev, [name]: true }));

    // Validate this specific field on blur
    const fieldErrors = validate();
    if (fieldErrors[name]) {
      setErrors(prev => ({ ...prev, [name]: fieldErrors[name] }));
    }
  }, [formData, aadharFrontFiles, aadharBackFiles, panFiles, rentalFiles]);

  const handleFieldChange = useCallback((name, value) => {
    if (name === "aadharNo") {
      value = value.replace(/\D/g, "").slice(0, 12);
    } else if (name === "alternativePhone" || name === "personalPhone") {
      value = value.replace(/\D/g, "").slice(0, 10);
    } else if (name === "panNo") {
      value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
    }

    // Mark field as touched when user interacts with it
    setTouched(prev => ({ ...prev, [name]: true }));

    // Clear error for this field when user types
    setErrors((prev) => {
      if (prev[name]) {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      }
      return prev;
    });

    // Only update if value has actually changed (using ref to avoid dependency issues)
    if (previousValuesRef.current[name] !== value) {
      previousValuesRef.current[name] = value;

      const updates = { [name]: value };

      // Auto-fill logic: if firstName or lastName changes, update aadharName and panName if they are empty
      if (name === "firstName" || name === "lastName") {
        const nextFirstName = name === "firstName" ? value : (formData.firstName || "");
        const nextLastName = name === "lastName" ? value : (formData.lastName || "");
        const fullName = `${nextFirstName} ${nextLastName}`.trim();

        if (!formData.aadharName) updates.aadharName = fullName;
        if (!formData.panName) updates.panName = fullName;
      }

      dispatch(setBasicDetails(updates));
    }
  }, [dispatch, formData.firstName, formData.lastName, formData.aadharName, formData.panName]);
  // File upload handlers
  const handleAadharFrontFileUpload = useCallback((event) => {
    const files = Array.from(event.target.files);
    setAadharFrontFiles(files);
    setErrors(prev => { const { aadharFrontFiles, ...rest } = prev; return rest; });
    setTouched(prev => { const { aadharFrontFiles, ...rest } = prev; return rest; });
  }, []);

  const handleAadharBackFileUpload = useCallback((event) => {
    const files = Array.from(event.target.files);
    setAadharBackFiles(files);
    setErrors(prev => { const { aadharBackFiles, ...rest } = prev; return rest; });
    setTouched(prev => { const { aadharBackFiles, ...rest } = prev; return rest; });
  }, []);

  const handlePanFileUpload = useCallback((event) => {
    const files = Array.from(event.target.files);
    setPanFiles(prev => [...prev, ...files]);
    setErrors(prev => { const { panFiles, ...rest } = prev; return rest; });
    setTouched(prev => { const { panFiles, ...rest } = prev; return rest; }); // ← add this
  }, []);

  const handlePassportFileUpload = useCallback((event) => {
    const files = Array.from(event.target.files);
    setPassportFiles(prev => [...prev, ...files]);
  }, []);

  const handleRentalFileUpload = useCallback((event) => {
    const files = Array.from(event.target.files);
    setRentalFiles(prev => [...prev, ...files]);
    setErrors(prev => { const { rentalAgreement, ...rest } = prev; return rest; });
    setTouched(prev => { const { rentalAgreement, ...rest } = prev; return rest; }); // ← add this
  }, []);

  const handleAddressBlur = useCallback((which, key, value) => {
    const errKey = `${which}_${key}`;

    // Mark address field as touched when user leaves it
    setTouched(prev => ({ ...prev, [errKey]: true }));

    // Validate this specific address field on blur
    const fieldErrors = validate();
    if (fieldErrors[errKey]) {
      setErrors(prev => ({ ...prev, [errKey]: fieldErrors[errKey] }));
    }
  }, [formData, aadharFrontFiles, aadharBackFiles, panFiles, rentalFiles]);

  const updateAddress = useCallback((which, key, value) => {
    const errKey = `${which}_${key}`;

    // Mark address field as touched when user interacts with it
    setTouched(prev => ({ ...prev, [errKey]: true }));

    setErrors((prev) => {
      if (prev[errKey]) {
        const newErrors = { ...prev };
        delete newErrors[errKey];
        return newErrors;
      }
      return prev;
    });
    // Immediate address update
    dispatch(updateAddressField({ addressType: which, field: key, value }));
  }, [dispatch]);

  const handleRemoveAadharFrontFile = useCallback((idx) => {
    setAadharFrontFiles([]);
  }, []);

  const handleRemoveAadharBackFile = useCallback((idx) => {
    setAadharBackFiles([]);
  }, []);

  const handleRemovePanFile = useCallback((idx) => {
    setPanFiles(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const handleRemovePassportFile = useCallback((idx) => {
    setPassportFiles(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const handleRemoveRentalFile = useCallback((idx) => {
    setRentalFiles(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const handleDisabilityCertificateUpload = useCallback((event) => {
    const files = Array.from(event.target.files);
    setDisabilityCertificateFiles(files);
    setErrors(prev => { const { disabilityCertificateFiles, ...rest } = prev; return rest; });
    setTouched(prev => { const { disabilityCertificateFiles, ...rest } = prev; return rest; });
  }, []);

  const handleRemoveDisabilityCertificateFile = useCallback(() => {
    setDisabilityCertificateFiles([]);
  }, []);

  const validate = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.firstName?.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName?.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.fatherName?.trim()) {
      newErrors.fatherName = "Father's name is required";
    }
    if (!formData.motherName?.trim()) {
      newErrors.motherName = "Mother's name is required";
    }

    if (!formData.gender) {
      newErrors.gender = "Gender is required";
    }

    if (!formData.dob) {
      newErrors.dob = "Date of birth is required";
    } else {
      const birthDate = new Date(formData.dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      if (age < 18) {
        newErrors.dob = "Employee must be at least 18 years old";
      }
    }

    if (!formData.bloodGroup) {
      newErrors.bloodGroup = "Blood group is required";
    }

    if (!formData.aadharNo?.trim()) {
      newErrors.aadharNo = "Aadhar number is required";
    } else if (!/^\d{12}$/.test(formData.aadharNo.replace(/\D/g, ''))) {
      newErrors.aadharNo = "Aadhar number must be 12 digits";
    }

    if (!formData.panNo?.trim()) {
      newErrors.panNo = "PAN number is required";
    } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNo.toUpperCase())) {
      newErrors.panNo = "Invalid PAN format (e.g., ABCDE1234F)";
    }

    if (!formData.aadharName?.trim()) {
      newErrors.aadharName = "Name as per Aadhar is required";
    }

    if (!formData.panName?.trim()) {
      newErrors.panName = "Name as per PAN is required";
    }

    if (formData.alternativePhone?.trim()) {
      if (!/^\d{10}$/.test(formData.alternativePhone.replace(/\D/g, ''))) {
        newErrors.alternativePhone = "Alternative phone number must be exactly 10 digits";
      }
    }

    const presentAddr = formData.presentAddress || {};
    if (!presentAddr.accommodationType) newErrors.presentAddress_accommodationType = "Accommodation type is required";
    if (!presentAddr.houseNo?.trim()) newErrors.presentAddress_houseNo = "House No is required";
    if (!presentAddr.area?.trim()) newErrors.presentAddress_area = "Area is required";
    if (!presentAddr.landmark?.trim()) newErrors.presentAddress_landmark = "Landmark is required";
    if (!presentAddr.city?.trim()) newErrors.presentAddress_city = "City is required";
    if (!presentAddr.state) newErrors.presentAddress_state = "State is required";
    if (!presentAddr.pincode?.trim()) newErrors.presentAddress_pincode = "Pincode is required";

    const permAddr = formData.permanentAddress || {};
    if (!permAddr.houseNo?.trim()) newErrors.permanentAddress_houseNo = "House No is required";
    if (!permAddr.area?.trim()) newErrors.permanentAddress_area = "Area is required";
    if (!permAddr.landmark?.trim()) newErrors.permanentAddress_landmark = "Landmark is required";
    if (!permAddr.city?.trim()) newErrors.permanentAddress_city = "City is required";
    if (!permAddr.state) newErrors.permanentAddress_state = "State is required";
    if (!permAddr.pincode?.trim()) newErrors.permanentAddress_pincode = "Pincode is required";

    if (aadharFrontFiles.length === 0) newErrors.aadharFrontFiles = "Aadhar Front copy is required";
    if (aadharBackFiles.length === 0) newErrors.aadharBackFiles = "Aadhar Back copy is required";
    if (panFiles.length === 0) newErrors.panFiles = "PAN document is required";

    if (formData.physicallyChallenged === "Yes") {
      if (!formData.physicallyChallengedDetails?.trim()) {
        newErrors.physicallyChallengedDetails = "Please provide physically challenged details";
      }
      if (disabilityCertificateFiles.length === 0) {
        newErrors.disabilityCertificateFiles = "Disability certificate copy is required";
      }
    }

    // Nominee Validation (Always mandatory, at least 1 nominee required)
    if (!nominees || nominees.length === 0) {
      newErrors.nomineesShare = "At least one nominee is required";
    } else {
      let totalShare = 0;
      nominees.forEach((n, idx) => {
        if (!n.nomineeName?.trim()) {
          newErrors[`nomineeName_${idx}`] = "Nominee name is required";
        }
        if (!n.relation) {
          newErrors[`nomineeRelation_${idx}`] = "Relationship is required";
        }
        if (!n.contactNo?.trim()) {
          newErrors[`nomineeContact_${idx}`] = "Contact number is required";
        } else if (!/^\d{10}$/.test(n.contactNo.replace(/\D/g, ''))) {
          newErrors[`nomineeContact_${idx}`] = "Contact number must be 10 digits";
        }
        if (!n.nomineeDob) {
          newErrors[`nomineeDob_${idx}`] = "Date of birth is required";
        } else if (new Date(n.nomineeDob) > new Date()) {
          newErrors[`nomineeDob_${idx}`] = "DOB cannot be in the future";
        }
        if (n.aadhaarNumber && !/^\d{12}$/.test(n.aadhaarNumber.replace(/\D/g, ''))) {
          newErrors[`nomineeAadhaarNumber_${idx}`] = "Aadhaar must be 12 digits";
        }
        if (!n.sharePercentage) {
          newErrors[`nomineeSharePercentage_${idx}`] = "Share percentage is required";
        }
        totalShare += parseFloat(n.sharePercentage) || 0;
      });
      if (Math.abs(totalShare - 100) > 0.01) {
        newErrors.nomineesShare = "Total share percentage of all nominees must be exactly 100%";
      }
    }

    setErrors(newErrors);
    dispatch(setBasicDetailsComplete(Object.keys(newErrors).length === 0));
    return newErrors;
  };

  const handleSubmit = async () => {
    // Run validation to get all errors
    const newErrors = validate();
    const hasErrors = Object.keys(newErrors).length > 0;

    const allRequiredFields = [
      'firstName', 'lastName', 'fatherName', 'motherName',
      'gender', 'dob', 'bloodGroup', 'aadharNo', 'panNo',
      'aadharName', 'panName',
      'alternativePhone',
      'presentAddress_accommodationType', 'presentAddress_houseNo',
      'presentAddress_area', 'presentAddress_landmark',
      'presentAddress_city', 'presentAddress_state', 'presentAddress_pincode',
      'aadharFrontFiles', 'aadharBackFiles', 'panFiles',
      ...(formData.physicallyChallenged === "Yes" ? ['physicallyChallengedDetails', 'disabilityCertificateFiles'] : []),
      'permanentAddress_houseNo',
      'permanentAddress_area', 'permanentAddress_landmark',
      'permanentAddress_city', 'permanentAddress_state', 'permanentAddress_pincode'
    ];

    if (nominees && nominees.length > 0) {
      nominees.forEach((_, idx) => {
        allRequiredFields.push(
          `nomineeName_${idx}`,
          `nomineeRelation_${idx}`,
          `nomineeContact_${idx}`,
          `nomineeDob_${idx}`,
          `nomineeSharePercentage_${idx}`,
          `nomineeAadhaarNumber_${idx}`
        );
      });
    }

    const allTouchedFields = {};
    allRequiredFields.forEach(field => {
      allTouchedFields[field] = true;
    });
    setTouched(allTouchedFields);

    if (hasErrors) {
      console.warn("Form validation failed with errors:", newErrors);
      showNotification("Please check the form and fill in all required fields correctly.", "error");
      return;
    }

    if (onNext) {
      if (!employeeId) {
        setErrors({ ...errors, submit: "Employee ID is missing" });
        return;
      }

      setIsSubmitting(true);
      try {
        const payload = new FormData();
        payload.append('data', JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          gender: formData.gender,
          dob: formData.dob,
          bloodGroup: formData.bloodGroup,
          fatherName: formData.fatherName,
          motherName: formData.motherName,
          alternativePhone: formData.alternativePhone,
          maritalStatus: formData.maritalStatus,
          spouseName: formData.spouseName,
          nationality: formData.nationality,
          religion: formData.religion,
          physicallyChallenged: formData.physicallyChallenged,
          physicallyChallengedDetails: formData.physicallyChallenged === "Yes" ? formData.physicallyChallengedDetails : "",
          presentAddress: formData.presentAddress,
          permanentAddress: formData.permanentAddress,
          sameAsPresent: false,
          aadharNo: formData.aadharNo,
          panNo: formData.panNo,
          passportNo: formData.passportNo,
          aadharName: formData.aadharName,
          panName: formData.panName,
          nominees: nominees.map(n => ({
            nomineeName: n.nomineeName,
            contactNo: n.contactNo,
            nomineeDob: n.nomineeDob,
            relation: n.relation,
            aadhaarNumber: n.aadhaarNumber,
            aadhaarFrontPath: n.aadhaarFrontPath || "",
            aadhaarBackPath: n.aadhaarBackPath || "",
            sharePercentage: n.sharePercentage,
            nomineeType: n.nomineeType || "Normal"
          })),
        }));

        if (aadharFrontFiles.length > 0 && !aadharFrontFiles[0].isExisting) {
          payload.append('aadharFrontDocument', aadharFrontFiles[0]);
        }
        if (aadharBackFiles.length > 0 && !aadharBackFiles[0].isExisting) {
          payload.append('aadharBackDocument', aadharBackFiles[0]);
        }
        if (panFiles.length > 0 && !panFiles[0].isExisting) {
          payload.append('panDocument', panFiles[0]);
        }
        if (passportFiles.length > 0 && !passportFiles[0].isExisting) {
          payload.append('additionalIdDocument', passportFiles[0]);
        }
        if (rentalFiles.length > 0 && !rentalFiles[0].isExisting) {
          payload.append('rentalAgreement', rentalFiles[0]);
        }
        if (disabilityCertificateFiles.length > 0 && !disabilityCertificateFiles[0].isExisting) {
          payload.append('disabilityCertificate', disabilityCertificateFiles[0]);
        }

        // Nominee documents
        nominees.forEach((n, idx) => {
          const front = nomineeFrontFiles[idx];
          if (front && front.length > 0 && !front[0].isExisting) {
            payload.append(`nominee_${idx}_aadhaarFront`, front[0]);
          }
          const back = nomineeBackFiles[idx];
          if (back && back.length > 0 && !back[0].isExisting) {
            payload.append(`nominee_${idx}_aadhaarBack`, back[0]);
          }
        });

        await saveEmployeePersonalInfo(employeeId, payload);

        // Mark personal information step as completed
        dispatch(setStepCompletion({ step: 'personalInformation', isCompleted: true }));

        // Mark newly uploaded files as existing so they don't get uploaded again
        if (panFiles.length > 0 && !panFiles[0].isExisting) {
          setPanFiles([{ name: panFiles[0].name, isExisting: true }]);
        }
        if (passportFiles.length > 0 && !passportFiles[0].isExisting) {
          setPassportFiles([{ name: passportFiles[0].name, isExisting: true }]);
        }
        if (rentalFiles.length > 0 && !rentalFiles[0].isExisting) {
          setRentalFiles([{ name: rentalFiles[0].name, isExisting: true }]);
        }
        if (disabilityCertificateFiles.length > 0 && !disabilityCertificateFiles[0].isExisting) {
          setDisabilityCertificateFiles([{ name: disabilityCertificateFiles[0].name, isExisting: true }]);
        }

        // Mark nominee files as existing
        const nextFronts = { ...nomineeFrontFiles };
        const nextBacks = { ...nomineeBackFiles };
        nominees.forEach((_, idx) => {
          if (nextFronts[idx] && nextFronts[idx].length > 0 && !nextFronts[idx][0].isExisting) {
            nextFronts[idx] = [{ name: nextFronts[idx][0].name, isExisting: true }];
          }
          if (nextBacks[idx] && nextBacks[idx].length > 0 && !nextBacks[idx][0].isExisting) {
            nextBacks[idx] = [{ name: nextBacks[idx][0].name, isExisting: true }];
          }
        });
        setNomineeFrontFiles(nextFronts);
        setNomineeBackFiles(nextBacks);

        setErrors({});
        setTouched({});

        if (employeeId) {
          showNotification("Personal details updated successfully", "success");
        }
        if (onNext) {
          onNext();
        }
      } catch (error) {
        console.error("Error saving personal info:", error);
        showNotification(error.message || "Failed to update personal details", "error");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSkip = () => {
    if (onNext) {
      onNext();
    }
  };



  // const NoteBox = () => (
  //   <Paper
  //     variant="outlined"
  //     sx={{
  //       px: 2,
  //       py: 1.5,
  //       borderRadius: 1.5,
  //       borderColor: "#D7C9FF",
  //       backgroundColor: "#F4F0FF",
  //       mb: 2.5,
  //     }}
  //   >
  //     <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
  //       Note:
  //     </Typography>
  //     <Typography variant="body2" sx={{ color: "#4F4937" }}>
  //       Please provide accurate personal information as it will appear on official documents and communications.
  //       Ensure the email address and phone number are correct for important notifications and contact purposes.
  //     </Typography>
  //   </Paper>
  // );

  // const FileListItem = React.memo(({ file, idx, onRemove }) => {
  //   const handleRemove = useCallback(() => {
  //     onRemove(idx);
  //   }, [idx, onRemove]);

  //   return (
  //     <Paper
  //       variant="outlined"
  //       sx={{
  //         p: 1,
  //         display: "flex",
  //         alignItems: "center",
  //         justifyContent: "space-between",
  //       }}
  //     >
  //       <Typography variant="body2">{file.name}</Typography>
  //       <IconButton size="small" onClick={handleRemove}>
  //         <CloseIcon fontSize="small" />
  //       </IconButton>
  //     </Paper>
  //   );
  // });



  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#030007', mb: 3.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          bgcolor: '#F5EEFC',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <PersonOutlineIcon sx={{ color: '#7522D1', fontSize: 18 }} />
        </Box>
        Employee Personal Details
      </Typography>

      <Grid container spacing={3}>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="First Name"
            name="firstName"
            value={formData?.firstName || ""}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={touched.firstName && !!errors.firstName}
            errorText={touched.firstName ? errors.firstName : ""}
            required
            placeholder="As per Aadhar"
          />
        </Grid>


        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Last Name"
            name="lastName"
            value={formData.lastName || ""}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={touched.lastName && !!errors.lastName}
            errorText={touched.lastName ? errors.lastName : ""}
            required
            placeholder="As per Aadhar"
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="select"
            label="Gender"
            name="gender"
            value={formData?.gender || ""}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={touched.gender && !!errors.gender}
            errorText={touched.gender ? errors.gender : ""}
            required
            options={[
              { value: "Male", label: "Male" },
              { value: "Female", label: "Female" },
              { value: "Non-binary", label: "Non-binary" },
              { value: "Prefer not to say", label: "Prefer not to say" },
            ]}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="date"
            label="Date of Birth"
            name="dob"
            value={formData?.dob || ""}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={touched.dob && !!errors.dob}
            errorText={touched.dob ? errors.dob : ""}
            required
            inputProps={{
              max: new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]
            }}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="select"
            label="Blood Group"
            name="bloodGroup"
            value={formData.bloodGroup || ""}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={touched.bloodGroup && !!errors.bloodGroup}
            errorText={touched.bloodGroup ? errors.bloodGroup : ""}
            required
            options={["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"]}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Father's Name"
            name="fatherName"
            value={formData.fatherName || ""}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={touched.fatherName && !!errors.fatherName}
            errorText={touched.fatherName ? errors.fatherName : ""}
            required
            placeholder="Enter father's name"
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Mother's Name"
            name="motherName"
            value={formData.motherName || ""}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={touched.motherName && !!errors.motherName}
            errorText={touched.motherName ? errors.motherName : ""}
            required
            placeholder="Enter mother's name"
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="email"
            label="Personal Email"
            name="personalEmail"
            value={formData.personalEmail || ""}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={touched.personalEmail && !!errors.personalEmail}
            errorText={touched.personalEmail ? errors.personalEmail : ""}
            placeholder="Enter personal email"
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="tel"
            label="Alternative Phone Number"
            name="alternativePhone"
            value={formData.alternativePhone || ""}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            numericOnly
            inputProps={{ maxLength: 10 }}
            error={touched.alternativePhone && !!errors.alternativePhone}
            errorText={touched.alternativePhone ? errors.alternativePhone : ""}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="select"
            label="Marital Status"
            name="maritalStatus"
            value={formData.maritalStatus || ""}
            onChange={handleFieldChange}
            options={["Single", "Married", "Widowed", "Separated"]}
          />
        </Grid>

        {formData.maritalStatus === "Married" && (
          <Grid item size={{ xs: 12, md: 6 }}>
            <DynamicFieldAdvanced
              type="text"
              label="Spouse Name"
              name="spouseName"
              value={formData.spouseName || ""}
              onChange={handleFieldChange}
            />
          </Grid>
        )}

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="select"
            label="Nationality"
            name="nationality"
            value={formData.nationality || "Indian"}
            onChange={handleFieldChange}
            options={["Indian", "Other"]}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="select"
            label="Religion"
            name="religion"
            value={formData.religion || ""}
            onChange={handleFieldChange}
            options={[
              "Hindu",
              "Muslim",
              "Christian",
              "Sikh",
              "Buddhist",
              "Jain",
              "Other",
            ]}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="select"
            label="Physically Challenged"
            name="physicallyChallenged"
            value={formData.physicallyChallenged || "No"}
            onChange={handleFieldChange}
            required
            options={["No", "Yes"]}
          />
        </Grid>

        {formData.physicallyChallenged === "Yes" && (
          <>
            <Grid item size={{ xs: 12, md: 6 }}>
              <DynamicFieldAdvanced
                type="textarea"
                label="Physically Challenged Details"
                name="physicallyChallengedDetails"
                value={formData.physicallyChallengedDetails || ""}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={touched.physicallyChallengedDetails && !!errors.physicallyChallengedDetails}
                errorText={touched.physicallyChallengedDetails ? errors.physicallyChallengedDetails : ""}
                required
                placeholder="Please provide details"
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: '#374151', fontSize: '0.825rem' }}>
                Disability Certificate Copy{' '}
                <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mt: 1 }}>
                <Button
                  component="label"
                  role={undefined}
                  variant="outlined"
                  tabIndex={-1}
                  startIcon={<CloudUploadIcon />}
                  size="small"
                  sx={{
                    borderColor: touched.disabilityCertificateFiles && errors.disabilityCertificateFiles ? '#EF4444' : 'rgba(117, 34, 209, 0.3)',
                    color: touched.disabilityCertificateFiles && errors.disabilityCertificateFiles ? '#EF4444' : '#7522D1',
                    backgroundColor: touched.disabilityCertificateFiles && errors.disabilityCertificateFiles ? 'rgba(239, 68, 68, 0.02)' : 'rgba(117, 34, 209, 0.02)',
                    '&:hover': {
                      borderColor: touched.disabilityCertificateFiles && errors.disabilityCertificateFiles ? '#EF4444' : '#7522D1',
                      backgroundColor: touched.disabilityCertificateFiles && errors.disabilityCertificateFiles ? 'rgba(239, 68, 68, 0.06)' : 'rgba(117, 34, 209, 0.06)',
                    },
                  }}
                >
                  Upload Certificate
                  <input
                    type="file"
                    onChange={handleDisabilityCertificateUpload}
                    style={visuallyHiddenStyles}
                  />
                </Button>
                <FileList
                  files={disabilityCertificateFiles}
                  onRemove={handleRemoveDisabilityCertificateFile}
                />
              </Box>
              {touched.disabilityCertificateFiles && errors.disabilityCertificateFiles && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', fontWeight: 600 }}>
                  {errors.disabilityCertificateFiles}
                </Typography>
              )}
            </Grid>
          </>
        )}
      </Grid>

      <Divider sx={{ my: 4, borderColor: 'rgba(117,34,209,0.08)' }} />

      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#030007', mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          bgcolor: '#F5EEFC',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <HomeOutlinedIcon sx={{ color: '#7522D1', fontSize: 18 }} />
        </Box>
        Present Address
      </Typography>
      <Grid container spacing={3}>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="select"
            label="Accommodation Type"
            name="accommodationType"
            value={formData.presentAddress?.accommodationType || ""}
            onChange={(name, value) => updateAddress("presentAddress", name, value)}
            onBlur={(name, value) => handleAddressBlur("presentAddress", name, value)}
            required
            options={["Rental", "Own", "Living with Parents"]}
            error={touched.presentAddress_accommodationType && !!errors.presentAddress_accommodationType}
            errorText={touched.presentAddress_accommodationType ? errors.presentAddress_accommodationType : ""}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Flat / House No"
            name="houseNo"
            value={formData.presentAddress?.houseNo || ""}
            onChange={(name, value) => updateAddress("presentAddress", name, value)}
            onBlur={(name, value) => handleAddressBlur("presentAddress", name, value)}
            required
            error={touched.presentAddress_houseNo && !!errors.presentAddress_houseNo}
            errorText={touched.presentAddress_houseNo ? errors.presentAddress_houseNo : ""}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Floor"
            name="floor"
            value={formData.presentAddress?.floor || ""}
            onChange={(name, value) => updateAddress("presentAddress", name, value)}
            onBlur={(name, value) => handleAddressBlur("presentAddress", name, value)}
            // Floor is NOT REQUIRED - removed required prop
            error={touched.presentAddress_floor && !!errors.presentAddress_floor}
            errorText={touched.presentAddress_floor ? errors.presentAddress_floor : ""}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Building / House Name"
            name="buildingName"
            value={formData.presentAddress?.buildingName || ""}
            onChange={(name, value) => updateAddress("presentAddress", name, value)}
            onBlur={(name, value) => handleAddressBlur("presentAddress", name, value)}
            // Building Name is NOT REQUIRED - removed required prop
            error={touched.presentAddress_buildingName && !!errors.presentAddress_buildingName}
            errorText={touched.presentAddress_buildingName ? errors.presentAddress_buildingName : ""}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Area/Street/Sector/Locality"
            name="area"
            value={formData.presentAddress?.area || ""}
            onChange={(name, value) => updateAddress("presentAddress", name, value)}
            onBlur={(name, value) => handleAddressBlur("presentAddress", name, value)}
            required
            error={touched.presentAddress_area && !!errors.presentAddress_area}
            errorText={touched.presentAddress_area ? errors.presentAddress_area : ""}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Nearby Landmark"
            name="landmark"
            value={formData.presentAddress?.landmark || ""}
            onChange={(name, value) => updateAddress("presentAddress", name, value)}
            onBlur={(name, value) => handleAddressBlur("presentAddress", name, value)}
            required
            error={touched.presentAddress_landmark && !!errors.presentAddress_landmark}
            errorText={touched.presentAddress_landmark ? errors.presentAddress_landmark : ""}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="City"
            name="city"
            value={formData.presentAddress?.city || ""}
            onChange={(name, value) => updateAddress("presentAddress", name, value)}
            onBlur={(name, value) => handleAddressBlur("presentAddress", name, value)}
            required
            error={touched.presentAddress_city && !!errors.presentAddress_city}
            errorText={touched.presentAddress_city ? errors.presentAddress_city : ""}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="autocomplete"
            label="State"
            name="state"
            value={formData.presentAddress?.state || ""}
            onChange={(name, value) => updateAddress("presentAddress", name, value)}
            onBlur={(name, value) => handleAddressBlur("presentAddress", name, value)}
            options={states}
            required
            error={touched.presentAddress_state && !!errors.presentAddress_state}
            errorText={touched.presentAddress_state ? errors.presentAddress_state : ""}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Pincode"
            name="pincode"
            value={formData.presentAddress?.pincode || ""}
            onChange={(name, value) => updateAddress("presentAddress", name, value)}
            onBlur={(name, value) => handleAddressBlur("presentAddress", name, value)}
            required
            numericOnly
            error={touched.presentAddress_pincode && !!errors.presentAddress_pincode}
            errorText={touched.presentAddress_pincode ? errors.presentAddress_pincode : ""}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4, borderColor: 'rgba(117,34,209,0.08)' }} />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: 'wrap',
          gap: 2,
          mb: 2.5,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#030007', display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            bgcolor: '#F5EEFC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <HomeOutlinedIcon sx={{ color: '#7522D1', fontSize: 18 }} />
          </Box>
          Permanent Address
        </Typography>
      </Box>
      <Grid container spacing={3}>
        {/* Removed Accommodation Type for Permanent Address */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Flat / House No"
            name="houseNo"
            value={formData.permanentAddress?.houseNo || ""}
            onChange={(name, value) => updateAddress("permanentAddress", name, value)}
            required
            error={touched.permanentAddress_houseNo && !!errors.permanentAddress_houseNo}
            errorText={touched.permanentAddress_houseNo ? errors.permanentAddress_houseNo : ""}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Floor"
            name="floor"
            value={formData.permanentAddress?.floor || ""}
            onChange={(name, value) => updateAddress("permanentAddress", name, value)}
            onBlur={(name, value) => handleAddressBlur("permanentAddress", name, value)}
            error={touched.permanentAddress_floor && !!errors.permanentAddress_floor}
            errorText={touched.permanentAddress_floor ? errors.permanentAddress_floor : ""}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Building / House Name"
            name="buildingName"
            value={formData.permanentAddress?.buildingName || ""}
            onChange={(name, value) => updateAddress("permanentAddress", name, value)}
            onBlur={(name, value) => handleAddressBlur("permanentAddress", name, value)}
            error={touched.permanentAddress_buildingName && !!errors.permanentAddress_buildingName}
            errorText={touched.permanentAddress_buildingName ? errors.permanentAddress_buildingName : ""}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Area/Street/Sector/Locality"
            name="area"
            value={formData.permanentAddress?.area || ""}
            onChange={(name, value) => updateAddress("permanentAddress", name, value)}
            required
            error={touched.permanentAddress_area && !!errors.permanentAddress_area}
            errorText={touched.permanentAddress_area ? errors.permanentAddress_area : ""}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Nearby Landmark"
            name="landmark"
            value={formData.permanentAddress?.landmark || ""}
            onChange={(name, value) => updateAddress("permanentAddress", name, value)}
            required
            error={touched.permanentAddress_landmark && !!errors.permanentAddress_landmark}
            errorText={touched.permanentAddress_landmark ? errors.permanentAddress_landmark : ""}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="City"
            name="city"
            value={formData.permanentAddress?.city || ""}
            onChange={(name, value) => updateAddress("permanentAddress", name, value)}
            required
            error={touched.permanentAddress_city && !!errors.permanentAddress_city}
            errorText={touched.permanentAddress_city ? errors.permanentAddress_city : ""}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="autocomplete"
            label="State"
            name="state"
            value={formData.permanentAddress?.state || ""}
            onChange={(name, value) => updateAddress("permanentAddress", name, value)}
            options={states}
            required
            error={touched.permanentAddress_state && !!errors.permanentAddress_state}
            errorText={touched.permanentAddress_state ? errors.permanentAddress_state : ""}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Pincode"
            name="pincode"
            value={formData.permanentAddress?.pincode || ""}
            onChange={(name, value) => updateAddress("permanentAddress", name, value)}
            required
            numericOnly
            error={touched.permanentAddress_pincode && !!errors.permanentAddress_pincode}
            errorText={touched.permanentAddress_pincode ? errors.permanentAddress_pincode : ""}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4, borderColor: 'rgba(117,34,209,0.08)' }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#030007', mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          bgcolor: '#F5EEFC',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <FingerprintIcon sx={{ color: '#7522D1', fontSize: 18 }} />
        </Box>
        Aadhar Details
      </Typography>
      <Grid container spacing={3}>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Aadhar No."
            name="aadharNo"
            value={formData.aadharNo || ""}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={touched.aadharNo && !!errors.aadharNo}
            errorText={touched.aadharNo ? errors.aadharNo : ""}
            required
            placeholder="12 digit Aadhar number"
            numericOnly
            inputProps={{ maxLength: 12 }}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Name as per Aadhar"
            name="aadharName"
            value={formData.aadharName || ""}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={touched.aadharName && !!errors.aadharName}
            errorText={touched.aadharName ? errors.aadharName : ""}
            required
          />
        </Grid>        <Grid item xs={12} md={6}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: '#374151', fontSize: '0.825rem' }}>
            Aadhar Front Copy{' '}
            <span style={{ color: '#EF4444' }}>*</span>
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mt: 1 }}>
            <Button
              component="label"
              role={undefined}
              variant="outlined"
              tabIndex={-1}
              startIcon={<CloudUploadIcon />}
              size="small"
              sx={{
                borderColor: touched.aadharFrontFiles && errors.aadharFrontFiles ? '#EF4444' : 'rgba(117, 34, 209, 0.3)',
                color: touched.aadharFrontFiles && errors.aadharFrontFiles ? '#EF4444' : '#7522D1',
                backgroundColor: touched.aadharFrontFiles && errors.aadharFrontFiles ? 'rgba(239, 68, 68, 0.02)' : 'rgba(117, 34, 209, 0.02)',
                '&:hover': {
                  borderColor: touched.aadharFrontFiles && errors.aadharFrontFiles ? '#EF4444' : '#7522D1',
                  backgroundColor: touched.aadharFrontFiles && errors.aadharFrontFiles ? 'rgba(239, 68, 68, 0.06)' : 'rgba(117, 34, 209, 0.06)',
                },
              }}
            >
              Upload Front
              <input
                type="file"
                onChange={handleAadharFrontFileUpload}
                style={visuallyHiddenStyles}
              />
            </Button>
            <FileList
              files={aadharFrontFiles}
              onRemove={handleRemoveAadharFrontFile}
            />
          </Box>
          {touched.aadharFrontFiles && errors.aadharFrontFiles && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', fontWeight: 600 }}>
              {errors.aadharFrontFiles}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: '#374151', fontSize: '0.825rem' }}>
            Aadhar Back Copy{' '}
            <span style={{ color: '#EF4444' }}>*</span>
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mt: 1 }}>
            <Button
              component="label"
              role={undefined}
              variant="outlined"
              tabIndex={-1}
              startIcon={<CloudUploadIcon />}
              size="small"
              sx={{
                borderColor: touched.aadharBackFiles && errors.aadharBackFiles ? '#EF4444' : 'rgba(117, 34, 209, 0.3)',
                color: touched.aadharBackFiles && errors.aadharBackFiles ? '#EF4444' : '#7522D1',
                backgroundColor: touched.aadharBackFiles && errors.aadharBackFiles ? 'rgba(239, 68, 68, 0.02)' : 'rgba(117, 34, 209, 0.02)',
                '&:hover': {
                  borderColor: touched.aadharBackFiles && errors.aadharBackFiles ? '#EF4444' : '#7522D1',
                  backgroundColor: touched.aadharBackFiles && errors.aadharBackFiles ? 'rgba(239, 68, 68, 0.06)' : 'rgba(117, 34, 209, 0.06)',
                },
              }}
            >
              Upload Back
              <input
                type="file"
                onChange={handleAadharBackFileUpload}
                style={visuallyHiddenStyles}
              />
            </Button>
            <FileList
              files={aadharBackFiles}
              onRemove={handleRemoveAadharBackFile}
            />
          </Box>
          {touched.aadharBackFiles && errors.aadharBackFiles && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', fontWeight: 600 }}>
              {errors.aadharBackFiles}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Divider sx={{ my: 4, borderColor: 'rgba(117,34,209,0.08)' }} />

      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#030007', mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          bgcolor: '#F5EEFC',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <CardMembershipIcon sx={{ color: '#7522D1', fontSize: 18 }} />
        </Box>
        Permanent Account Number (PAN)
      </Typography>
      <Grid container spacing={3}>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="PAN No."
            name="panNo"
            value={formData.panNo || ""}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={touched.panNo && !!errors.panNo}
            errorText={touched.panNo ? errors.panNo : ""}
            required
            placeholder="e.g., ABCDE1234F"
            inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Name as per PAN"
            name="panName"
            value={formData.panName || ""}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={touched.panName && !!errors.panName}
            errorText={touched.panName ? errors.panName : ""}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: '#374151', fontSize: '0.825rem' }}>
            Attachments{' '}
            <span style={{ color: '#EF4444' }}>*</span>
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Button
              component="label"
              role={undefined}
              variant="outlined"
              tabIndex={-1}
              startIcon={<CloudUploadIcon />}
              size="small"
              sx={{
                borderColor: touched.panFiles && errors.panFiles ? '#EF4444' : 'rgba(117, 34, 209, 0.3)',
                color: touched.panFiles && errors.panFiles ? '#EF4444' : '#7522D1',
                backgroundColor: touched.panFiles && errors.panFiles ? 'rgba(239, 68, 68, 0.02)' : 'rgba(117, 34, 209, 0.02)',
                '&:hover': {
                  borderColor: touched.panFiles && errors.panFiles ? '#EF4444' : '#7522D1',
                  backgroundColor: touched.panFiles && errors.panFiles ? 'rgba(239, 68, 68, 0.06)' : 'rgba(117, 34, 209, 0.06)',
                },
              }}
            >
              Upload files
              <input
                type="file"
                multiple
                onChange={handlePanFileUpload}
                style={visuallyHiddenStyles}
              />
            </Button>
            <FileList
              files={panFiles}
              onRemove={handleRemovePanFile}
            />
          </Box>
          {touched.panFiles && errors.panFiles && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', fontWeight: 600 }}>
              {errors.panFiles}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Divider sx={{ my: 4, borderColor: 'rgba(117,34,209,0.08)' }} />

      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#030007', mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          bgcolor: '#F5EEFC',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <AssignmentIndOutlinedIcon sx={{ color: '#7522D1', fontSize: 18 }} />
        </Box>
        Passport Details (Optional)
      </Typography>
      <Grid container spacing={3}>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Passport No."
            name="passportNo"
            value={formData.passportNo || ""}
            onChange={handleFieldChange}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Name in Passport"
            name="passportName"
            value={formData.passportName || ""}
            onChange={handleFieldChange}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="date"
            label="Expiry Date"
            name="passportExpiry"
            value={formData.passportExpiry || ""}
            onChange={handleFieldChange}
          />
        </Grid>
        <Grid item size={{ xs: 12 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: '#374151', fontSize: '0.825rem' }}>
            Attachments
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Button
              component="label"
              role={undefined}
              variant="outlined"
              tabIndex={-1}
              startIcon={<CloudUploadIcon />}
              size="small"
              sx={{
                borderColor: 'rgba(117, 34, 209, 0.3)',
                color: '#7522D1',
                backgroundColor: 'rgba(117, 34, 209, 0.02)',
                '&:hover': {
                  borderColor: '#7522D1',
                  backgroundColor: 'rgba(117, 34, 209, 0.06)',
                },
              }}
            >
              Upload files
              <input
                type="file"
                multiple
                onChange={handlePassportFileUpload}
                style={visuallyHiddenStyles}
              />
            </Button>
            <FileList
              files={passportFiles}
              onRemove={handleRemovePassportFile}
            />
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4, borderColor: 'rgba(117,34,209,0.08)' }} />

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5, mt: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#030007', display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            bgcolor: '#F5EEFC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <PersonOutlineIcon sx={{ color: '#7522D1', fontSize: 18 }} />
          </Box>
          Nominee Details
        </Typography>
        <Button
          variant="outlined"
          onClick={handleAddNominee}
          sx={{
            borderColor: 'rgba(117, 34, 209, 0.3)',
            color: '#7522D1',
            backgroundColor: 'rgba(117, 34, 209, 0.02)',
            '&:hover': {
              borderColor: '#7522D1',
              backgroundColor: 'rgba(117, 34, 209, 0.06)',
            },
          }}
        >
          Add Nominee
        </Button>
      </Box>

      {nominees.length === 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            mb: 4,
            textAlign: "center",
            borderColor: "rgba(117,34,209,0.08)",
            borderRadius: 2,
            bgcolor: "rgba(117,34,209,0.01)"
          }}
        >
          <Typography variant="body2" color="textSecondary">
            No nominees added yet. Click "Add Nominee" to add one.
          </Typography>
        </Paper>
      )}

      {nominees.map((nominee, idx) => (
        <Paper
          key={idx}
          variant="outlined"
          sx={{
            p: 3,
            mb: 3,
            borderColor: "rgba(117,34,209,0.12)",
            borderRadius: 2,
            position: "relative",
            bgcolor: "rgba(117,34,209,0.015)"
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#7522D1' }}>
              Nominee #{idx + 1}
            </Typography>
            {nominees.length > 1 && (
              <Button
                variant="text"
                color="error"
                size="small"
                onClick={() => handleRemoveNominee(idx)}
              >
                Remove
              </Button>
            )}
          </Box>

          <Grid container spacing={3}>
            <Grid item size={{ xs: 12, md: 6 }}>
              <DynamicFieldAdvanced
                type="text"
                label="Nominee Name"
                name={`nomineeName_${idx}`}
                value={nominee.nomineeName || ""}
                onChange={(name, value) => handleNomineeChange(idx, "nomineeName", value)}
                onBlur={() => {
                  setTouched(prev => ({ ...prev, [`nomineeName_${idx}`]: true }));
                  validate();
                }}
                error={touched[`nomineeName_${idx}`] && !!errors[`nomineeName_${idx}`]}
                errorText={touched[`nomineeName_${idx}`] ? errors[`nomineeName_${idx}`] : ""}
                required
                placeholder="Enter nominee name"
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <DynamicFieldAdvanced
                type="select"
                label="Relationship"
                name={`relation_${idx}`}
                value={nominee.relation || ""}
                onChange={(name, value) => handleNomineeChange(idx, "relation", value)}
                onBlur={() => {
                  setTouched(prev => ({ ...prev, [`nomineeRelation_${idx}`]: true }));
                  validate();
                }}
                error={touched[`nomineeRelation_${idx}`] && !!errors[`nomineeRelation_${idx}`]}
                errorText={touched[`nomineeRelation_${idx}`] ? errors[`nomineeRelation_${idx}`] : ""}
                required
                options={["Father", "Mother", "Spouse", "Sibling", "Child", "Other"]}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <DynamicFieldAdvanced
                type="text"
                label="Contact No."
                name={`contactNo_${idx}`}
                value={nominee.contactNo || ""}
                onChange={(name, value) => handleNomineeChange(idx, "contactNo", value)}
                onBlur={() => {
                  setTouched(prev => ({ ...prev, [`nomineeContact_${idx}`]: true }));
                  validate();
                }}
                error={touched[`nomineeContact_${idx}`] && !!errors[`nomineeContact_${idx}`]}
                errorText={touched[`nomineeContact_${idx}`] ? errors[`nomineeContact_${idx}`] : ""}
                required
                placeholder="10 digit contact number"
                numericOnly
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <DynamicFieldAdvanced
                type="date"
                label="Date of Birth"
                name={`nomineeDob_${idx}`}
                value={nominee.nomineeDob || ""}
                onChange={(name, value) => handleNomineeChange(idx, "nomineeDob", value)}
                onBlur={() => {
                  setTouched(prev => ({ ...prev, [`nomineeDob_${idx}`]: true }));
                  validate();
                }}
                error={touched[`nomineeDob_${idx}`] && !!errors[`nomineeDob_${idx}`]}
                errorText={touched[`nomineeDob_${idx}`] ? errors[`nomineeDob_${idx}`] : ""}
                required
                inputProps={{
                  max: new Date().toISOString().split('T')[0]
                }}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <DynamicFieldAdvanced
                type="text"
                label="Aadhaar Number (Optional)"
                name={`aadhaarNumber_${idx}`}
                value={nominee.aadhaarNumber || ""}
                onChange={(name, value) => handleNomineeChange(idx, "aadhaarNumber", value)}
                onBlur={() => {
                  setTouched(prev => ({ ...prev, [`nomineeAadhaarNumber_${idx}`]: true }));
                  validate();
                }}
                error={touched[`nomineeAadhaarNumber_${idx}`] && !!errors[`nomineeAadhaarNumber_${idx}`]}
                errorText={touched[`nomineeAadhaarNumber_${idx}`] ? errors[`nomineeAadhaarNumber_${idx}`] : ""}
                placeholder="12 digit Aadhaar number"
                numericOnly
                inputProps={{ maxLength: 12 }}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <DynamicFieldAdvanced
                type="text"
                label="Share Percentage (%)"
                name={`sharePercentage_${idx}`}
                value={nominee.sharePercentage || ""}
                onChange={(name, value) => handleNomineeChange(idx, "sharePercentage", value)}
                onBlur={() => {
                  setTouched(prev => ({ ...prev, [`nomineeSharePercentage_${idx}`]: true }));
                  validate();
                }}
                error={touched[`nomineeSharePercentage_${idx}`] && !!errors[`nomineeSharePercentage_${idx}`]}
                errorText={touched[`nomineeSharePercentage_${idx}`] ? errors[`nomineeSharePercentage_${idx}`] : ""}
                required
                placeholder="e.g. 100"
                numericOnly
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: '#374151', fontSize: '0.825rem' }}>
                Aadhaar Front Copy (Optional)
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mt: 1 }}>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: 'rgba(117, 34, 209, 0.3)',
                    color: '#7522D1',
                    backgroundColor: 'rgba(117, 34, 209, 0.02)',
                    '&:hover': {
                      borderColor: '#7522D1',
                      backgroundColor: 'rgba(117, 34, 209, 0.06)',
                    },
                  }}
                >
                  Upload Front
                  <input
                    type="file"
                    onChange={(e) => handleNomineeFrontUpload(idx, e)}
                    style={visuallyHiddenStyles}
                  />
                </Button>
                <FileList
                  files={nomineeFrontFiles[idx] || []}
                  onRemove={() => handleRemoveNomineeFrontFile(idx)}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: '#374151', fontSize: '0.825rem' }}>
                Aadhaar Back Copy (Optional)
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mt: 1 }}>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: 'rgba(117, 34, 209, 0.3)',
                    color: '#7522D1',
                    backgroundColor: 'rgba(117, 34, 209, 0.02)',
                    '&:hover': {
                      borderColor: '#7522D1',
                      backgroundColor: 'rgba(117, 34, 209, 0.06)',
                    },
                  }}
                >
                  Upload Back
                  <input
                    type="file"
                    onChange={(e) => handleNomineeBackUpload(idx, e)}
                    style={visuallyHiddenStyles}
                  />
                </Button>
                <FileList
                  files={nomineeBackFiles[idx] || []}
                  onRemove={() => handleRemoveNomineeBackFile(idx)}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      ))}
      {errors.nomineesShare && (
        <Typography variant="caption" color="error" sx={{ mt: 1, mb: 2, display: 'block', fontWeight: 600 }}>
          {errors.nomineesShare}
        </Typography>
      )}

      <Box sx={{ mt: 5, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={handleSkip}
          size="large"
          disabled={isSubmitting}
        >
          Skip for now
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          size="large"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving..." : (employeeId ? "Update" : "Save & Next")}
        </Button>
      </Box>
      <Notification />
    </Box>
  );
};

export default PersonalDetails;
